import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../../services/api";
import { EggRoutingRule } from "../../types";
import EggRuleForm, { EggRuleFormData, formDataToPayload, ruleToFormData } from "./EggRuleForm";
import EggRuleAuditLog from "./EggRuleAuditLog";

export default function EggRuleDetail() {
  const { ruleId } = useParams<{ ruleId: string }>();
  const navigate = useNavigate();

  const [rule, setRule] = useState<EggRoutingRule | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const formDefaults = useMemo(() => (rule ? ruleToFormData(rule) : undefined), [rule]);

  useEffect(() => {
    if (!ruleId) return;
    const fetch = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await apiClient.get(`/v1/egg/rules/${ruleId}`);
        setRule(res.data.data ?? res.data);
      } catch (e) {
        console.error(e);
        setLoadError("Impossibile caricare la regola");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [ruleId]);

  const handleSubmit = async (data: EggRuleFormData) => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await apiClient.patch(
        `/v1/egg/rules/${ruleId}`,
        formDataToPayload(data, { forUpdate: true })
      );
      setRule(res.data.data ?? res.data);
      setSaveSuccess(true);
    } catch (e) {
      console.error(e);
      setSaveError("Errore durante il salvataggio della regola");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/v1/egg/rules/${ruleId}`);
      navigate("/egg/rules");
    } catch (e) {
      console.error(e);
      setDeleteOpen(false);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !rule) {
    return (
      <Box sx={{ maxWidth: 800, mx: "auto", py: 3 }}>
        <Alert severity="error">{loadError ?? "Regola non trovata"}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", py: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        {rule.name}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Regola aggiornata con successo
          </Alert>
        )}
        <EggRuleForm
          defaultValues={formDefaults}
          onSubmit={handleSubmit}
          isLoading={saving}
          submitLabel="Salva modifiche"
          submitError={saveError}
        />
      </Paper>

      {/* Audit log */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <EggRuleAuditLog ruleId={ruleId!} />
      </Paper>

      {/* Danger zone */}
      <Paper
        sx={{
          p: 3,
          border: "1px solid",
          borderColor: "error.main",
        }}
      >
        <Typography variant="subtitle1" color="error" sx={{ fontWeight: 700, mb: 1 }}>
          Zona pericolosa
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Elimina questa regola
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Questa azione è irreversibile. La regola verrà rimossa definitivamente.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteOpen(true)}
          >
            Elimina regola
          </Button>
        </Box>
      </Paper>

      {/* Dialog conferma eliminazione */}
      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)}>
        <DialogTitle>Elimina regola</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare la regola <strong>{rule.name}</strong>?
            Questa operazione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Annulla
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? "Eliminazione..." : "Elimina"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
